/**
 * Figma: https://www.figma.com/design/XwMQV9iBJ1Ec4sOzWMEZcK/-Dev--Customer-Hub-2.3-%E2%80%93-Phase-1?node-id=2271-119811&m=dev
 */

import { useEffect, useRef, useState } from 'react'

import { useI18N } from '@susu/i18n/client'
import { Icon } from '@susu/ui/components'
import { keepPreviousData, useQuery } from '@tanstack/react-query'

import {
  enrichItemsInCartItems,
  fetchItemsInCartItems,
  type ItemsInCartApiItem,
} from '../api/itemsInCartApi'
import { useHasBootSkeleton } from '../contexts/BootSkeletonContext'
import { useCopyProductCode } from '../hooks/useCopyProductCode'
import { useSkeletonPhase } from '../hooks/useSkeletonPhase'
import { getItemsInCartMaxItems } from '../lib/growthbook'
import { cn } from '../lib/utils'
import { normalizeEnvironment } from '../utils/normalizeEnv'

import ItemsSectionSkeleton from './ItemsSectionSkeleton'

import type { EnvironmentWithChina } from '../App'
import type { Role } from '../contexts/CustomerHubContext'

interface ItemsInCartProps {
  accountNumber: string
  authToken?: string
  env: EnvironmentWithChina
  locationId: string
  onLoaded?: () => void
  role?: Role
}

function getItemName(item: ItemsInCartApiItem): string {
  return (
    item.CommercialDescription?.trim() ||
    item.commercialDescription?.trim() ||
    item.identifier?.trim() ||
    item.variant_id?.trim() ||
    ''
  )
}

function getItemFabric(item: ItemsInCartApiItem): string {
  return item.fabric?.trim() || ''
}

function getItemVariantId(item: ItemsInCartApiItem): string {
  return item.variant_id?.trim() || ''
}

function getItemImageUrl(item: ItemsInCartApiItem): string | undefined {
  const imageUrl = item.ImageURL || item.imageUrl || item.image_url
  const normalizedImageUrl = imageUrl?.trim()

  return normalizedImageUrl || undefined
}

export default function ItemsInCart({
  accountNumber,
  authToken,
  env,
  locationId,
  onLoaded,
  role,
}: ItemsInCartProps) {
  const { t } = useI18N()
  const loadedTrackingKeyRef = useRef<string | null>(null)
  const isStyleAdvisor = role === 'style-advisor'
  const { data: maxItems, isLoading: isMaxItemsLoading } = useQuery({
    queryKey: ['itemsInCartMaxItems', env, locationId],
    queryFn: () =>
      getItemsInCartMaxItems(normalizeEnvironment(env), locationId, role),
    enabled: isStyleAdvisor,
    staleTime: 5 * 60 * 1000,
  })
  const shouldFetchItems =
    isStyleAdvisor &&
    Boolean(accountNumber) &&
    typeof maxItems === 'number' &&
    maxItems > 0
  const {
    data: cartItems = [],
    isLoading: isCartItemsLoading,
    isError: isCartItemsError,
  } = useQuery({
    queryKey: ['itemsInCart', 'cartItems', accountNumber, env],
    queryFn: () => fetchItemsInCartItems(accountNumber, env, authToken),
    enabled: shouldFetchItems,
    staleTime: 60 * 1000,
  })
  const visibleCartItems = cartItems.slice(0, maxItems ?? 0)
  const shouldFetchItemDetails =
    shouldFetchItems && !isCartItemsLoading && visibleCartItems.length > 0
  const {
    data: itemsWithDetails,
    isLoading: isItemDetailsLoading,
    isError: isItemDetailsError,
  } = useQuery({
    queryKey: [
      'itemsInCart',
      'itemDetails',
      accountNumber,
      env,
      visibleCartItems.map(
        (item) => item.identifier || item.variant_id || item.ProductID || '',
      ),
    ],
    queryFn: () =>
      enrichItemsInCartItems(visibleCartItems, accountNumber, env, authToken),
    enabled: shouldFetchItemDetails,
    staleTime: 60 * 1000,
    // A background refetch that changes the cart also changes this query's
    // key; keeping the previous enrichment visible stops the section from
    // dropping back to a skeleton over already-shown content.
    placeholderData: keepPreviousData,
  })
  const visibleItems = isItemDetailsError
    ? visibleCartItems
    : (itemsWithDetails ?? visibleCartItems)
  // Pending until every query this section depends on has settled. The
  // details query enables one render after the cart query lands, so it is
  // derived from data presence rather than chained `isLoading` flags — the
  // skeleton phase must not blip to content in that gap.
  const isSectionPending =
    isStyleAdvisor &&
    (isMaxItemsLoading ||
      isCartItemsLoading ||
      (shouldFetchItemDetails &&
        itemsWithDetails === undefined &&
        !isItemDetailsError))
  const hasBootSkeleton = useHasBootSkeleton()
  const { phase, contentClassName } = useSkeletonPhase(isSectionPending, {
    skipDelay: hasBootSkeleton,
  })

  useEffect(() => {
    if (
      !isStyleAdvisor ||
      isCartItemsLoading ||
      isCartItemsError ||
      isItemDetailsLoading ||
      visibleItems.length === 0
    ) {
      return
    }

    const trackingKey = `${accountNumber}-${env}-${visibleItems
      .map((item) => item.identifier || item.variant_id || item.ProductID || '')
      .join('|')}`

    if (loadedTrackingKeyRef.current === trackingKey) {
      return
    }

    loadedTrackingKeyRef.current = trackingKey
    onLoaded?.()
  }, [
    accountNumber,
    env,
    isCartItemsError,
    isCartItemsLoading,
    isItemDetailsLoading,
    isStyleAdvisor,
    onLoaded,
    visibleItems,
  ])

  if (!isStyleAdvisor || isCartItemsError) {
    return null
  }

  // Exact-layout skeleton, heading included, until the anti-flash timing
  // allows the swap. `reserve` keeps the layout without painting anything, so
  // a fast/cached response never flashes a skeleton.
  if (phase !== 'content') {
    return (
      <ItemsSectionSkeleton
        testId="cart-section-skeleton"
        isReserved={phase === 'reserve'}
      />
    )
  }

  // Settled and empty (or gated off): the whole section leaves in one reflow.
  if (maxItems === undefined || maxItems === 0 || cartItems.length === 0) {
    return null
  }

  return (
    <section
      className={cn('flex flex-col gap-16 max-lg:px-16', contentClassName)}
    >
      <h2 className="text-xl leading-xl font-medium text-text tracking-heading">
        {t('cart.title')}
      </h2>
      <div className="flex flex-col gap-16">
        {visibleItems.map((item, index) => (
          <CartItemCard
            key={`${getItemName(item) || item.variant_id || item.identifier || 'cart-item'}-${index}`}
            item={item}
            showVariantId={isStyleAdvisor}
          />
        ))}
      </div>
    </section>
  )
}

function CartItemCard({
  item,
  showVariantId,
}: {
  item: ItemsInCartApiItem
  showVariantId: boolean
}) {
  const { t } = useI18N()
  const [imageError, setImageError] = useState(false)
  const itemName = getItemName(item)
  const itemFabric = getItemFabric(item)
  const itemVariantId = getItemVariantId(item)
  const imageUrl = getItemImageUrl(item)
  const shouldShowImage = imageUrl && !imageError
  const { canCopy, clickableProps } = useCopyProductCode(itemVariantId)

  return (
    <article
      className={cn(
        'flex min-w-[340px] w-full items-center gap-16 bg-surface',
        canCopy && 'cursor-pointer',
      )}
      data-testid="cart-item-card"
      {...clickableProps}
    >
      <div className="relative h-[156px] w-[130px] shrink-0 overflow-hidden rounded-sm bg-gray-200 p-16">
        {shouldShowImage ? (
          <img
            src={imageUrl}
            alt={itemName}
            loading="lazy"
            decoding="async"
            onError={() => setImageError(true)}
            className="h-full w-full object-contain object-center"
          />
        ) : (
          <svg
            viewBox="0 0 40 40"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="h-full w-full"
          >
            <title>{t('cart.noImage')}</title>
            <rect width="40" height="40" rx="4" fill="#E5E5E5" />
            <path d="M12 28L17 23L21 27L26 22L28 24V28H12Z" fill="#C6C6C6" />
            <circle cx="24" cy="16" r="3" fill="#C6C6C6" />
          </svg>
        )}
      </div>
      <div className="flex min-w-0 flex-1 flex-col justify-center gap-4 self-stretch">
        <p className="truncate text-base leading-base font-medium text-text">
          {itemName}
        </p>
        {itemFabric && (
          <p className="truncate text-xs leading-xs font-light text-text">
            {itemFabric}
          </p>
        )}
        {showVariantId && itemVariantId && (
          <p className="flex min-w-0 items-center gap-4 text-xs leading-xs font-light text-text-secondary">
            <span className="truncate">{itemVariantId.toUpperCase()}</span>
            {canCopy && (
              <span
                className="shrink-0"
                data-testid="copy-product-code-icon"
                aria-hidden="true"
              >
                <Icon name="copy" size={16} color="secondary" />
              </span>
            )}
          </p>
        )}
      </div>
    </article>
  )
}
